import React, { useState } from "react";

import { useNavigate } from "react-router-dom";

import { useAuth } from "@/hooks/useAuth";

import { getRandomDefaultColor } from "@/utils/UColors";

import { OnboardingDefaultRhythm } from "@/constants/index";

import {
	TOnboardingCompanyValues,
	TOnboardingOptionalStep,
	TOnboardingProjectValues,
	TOnboardingResult,
	TOnboardingScreen,
	TOnboardingStep,
	TOnboardingValues,
} from "@/types";

import { ONBOARDING_STEPS } from "./COnboarding";
import { CompanyStep } from "./steps/CompanyStep";
import { DoneStep } from "./steps/DoneStep";
import { ProjectStep } from "./steps/ProjectStep";
import { RhythmStep } from "./steps/RhythmStep";
import { WelcomeStep } from "./steps/WelcomeStep";
import { useOnboardingSubmit } from "./useOnboardingSubmit";

const emptyCompany: TOnboardingCompanyValues = {
	name: "",
	siren: "",
	vatNumber: "",
	email: "",
	address: "",
	postalCode: "",
	city: "",
	logo: null,
};

const emptyProject = (): TOnboardingProjectValues => ({
	name: "",
	color: getRandomDefaultColor(),
	description: "",
	clientName: "",
});

const previousOf = (step: TOnboardingStep): TOnboardingScreen => {
	const index = ONBOARDING_STEPS.indexOf(step);
	return index === 0 ? "welcome" : ONBOARDING_STEPS[index - 1];
};

const nextOf = (step: TOnboardingStep): TOnboardingScreen => {
	const index = ONBOARDING_STEPS.indexOf(step);
	return index === ONBOARDING_STEPS.length - 1
		? "done"
		: ONBOARDING_STEPS[index + 1];
};

/**
 * Guided first-run setup: project (required) → rhythm → company (both
 * skippable). Each step is its own Formik form; what it
 * collects lands in `values` here and nothing is sent to the API until
 * the last step submits (see useOnboardingSubmit). Going back keeps what
 * was typed; skipping an optional step clears its slice so a skipped
 * step is really skipped even if it was filled earlier.
 */
export const Onboarding: React.FC = () => {
	const navigate = useNavigate();
	const { user } = useAuth();
	const { submit, isSubmitting } = useOnboardingSubmit();

	const [screen, setScreen] = useState<TOnboardingScreen>("welcome");
	// Lazy so the random colours are drawn once, not on every render.
	const [values, setValues] = useState<TOnboardingValues>(() => ({
		project: emptyProject(),
	}));
	// Drafts of the optional steps survive a skip → back → fill round
	// trip, while `values` only holds what will actually be created.
	const [drafts, setDrafts] = useState<
		Pick<TOnboardingValues, TOnboardingOptionalStep>
	>({});
	const [result, setResult] = useState<TOnboardingResult | null>(null);

	const finish = (finalValues: TOnboardingValues) =>
		submit(finalValues).then(
			(res) => {
				setResult(res);
				setScreen("done");
			},
			// The toast already showed the error; stay on the step so the
			// user can retry or fix what was typed.
			() => undefined
		);

	const goNext = (step: TOnboardingStep, nextValues: TOnboardingValues) => {
		setValues(nextValues);
		const next = nextOf(step);
		if (next === "done") return finish(nextValues);
		setScreen(next);
	};

	const skip = (step: TOnboardingOptionalStep) =>
		goNext(step, { ...values, [step]: undefined });

	switch (screen) {
		case "welcome":
			return (
				<WelcomeStep
					firstName={user?.firstName}
					onStart={() => setScreen("project")}
				/>
			);
		case "project":
			return (
				<ProjectStep
					initialValues={values.project}
					onBack={() => setScreen(previousOf("project"))}
					onNext={(project) =>
						goNext("project", { ...values, project })
					}
				/>
			);
		case "rhythm":
			return (
				<RhythmStep
					initialValues={
						values.rhythm ??
						drafts.rhythm ??
						OnboardingDefaultRhythm
					}
					onBack={() => setScreen(previousOf("rhythm"))}
					onSkip={() => skip("rhythm")}
					onNext={(rhythm) => {
						setDrafts((d) => ({ ...d, rhythm }));
						goNext("rhythm", { ...values, rhythm });
					}}
				/>
			);
		case "company":
			return (
				<CompanyStep
					initialValues={
						values.company ?? drafts.company ?? emptyCompany
					}
					onBack={() => setScreen(previousOf("company"))}
					onSkip={() => skip("company")}
					onNext={(company) => {
						setDrafts((d) => ({ ...d, company }));
						goNext("company", { ...values, company });
					}}
					isSubmitting={isSubmitting}
				/>
			);
		case "done":
			return result ? (
				<DoneStep result={result} onFinish={() => navigate("/")} />
			) : null;
	}
};
